/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {generateUUID, ResponseData} from '@dvrd/dvr-controls';
import React, {useContext, useEffect, useState} from 'react';
import {CustomerType, InvoiceRuleType, InvoiceUploadRule} from '../../util/interfaces';
import {AppContext} from '../misc/appContext';
import Account from '../../models/accountModel';
import {createErrorMessage} from '../../util/utils';
import InvoiceUploadSettings from './invoiceUploadSettings';
import PageChangePermission from '../misc/pageChangePermission';
import {PermissionCallback} from '../../util/pageChanger';
import {showDialog, showSnackbar} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

interface Props {
    noSubTitle?: boolean;
}

export default function InvoiceUploadSettingsController(props: Props) {
    const context = useContext(AppContext);
    const {account} = context.accountContext;
    const {user} = context.userContext;
    const {t} = useTranslation();
    const {noSubTitle} = props;
    const [loading, setLoading] = useState(false);
    const [proactiveEnabled, setProactiveEnabled] = useState(account?.settings.proactivelyUploadInvoices ?? false);
    const [respondEnabled, setRespondEnabled] = useState(account?.settings.autoRespondInvoiceRequests ?? false);
    const [respondOldEnabled, setRespondOldEnabled] = useState(account?.settings.autoRespondOldOrders ?? false);
    const [notificationEnabled, setNotificationEnabled] = useState(account?.settings.notifyMissingInvoice ?? false);
    const [notifyMail, setNotifyMail] = useState((account?.settings.notifyMissingInvoiceMail || user?.email) ?? '');
    const [oldNumPrefix, setOldNumPrefix] = useState(account?.settings.oldOrderInvoicePrefix ?? '{jaar}pre-');
    const [oldNumStart, setOldNumStart] = useState(account?.settings.oldOrderInvoiceStart ?? '0001');
    const [rules, setRules] = useState<Array<InvoiceUploadRule>>([]);
    const [hasChanged, setHasChanged] = useState(false);

    function onChangeNotifyMail(value: string) {
        if (value === notifyMail) return;
        setNotifyMail(value);
        setHasChanged(true);
    }

    function onAddRule() {
        setHasChanged(true);
        setRules(
            rules.concat({
                id: generateUUID(),
                rule_type: InvoiceRuleType.WHITELIST,
                customer_type: CustomerType.BOTH,
                countries: null
            })
        );
    }

    function onDeleteRule(rule: InvoiceUploadRule) {
        return function () {
            showDialog(
                t('invoiceUploadSettings.deleteRuleConfirm', 'Weet je zeker dat je deze regel wilt verwijderen?'),
                t('invoiceUploadSettings.deleteRuleTitle', 'Regel verwijderen'),
                [t('common.no', 'Nee'), {label: t('common.yes', 'Ja'), onClick: onConfirmDelete(rule), primary: false}]
            );
        };
    }

    function onConfirmDelete(rule: InvoiceUploadRule) {
        return function () {
            const ruleId = rule.id;
            const _rules = rules.slice();
            for (let i = 0; i < _rules.length; i++) {
                if (_rules[i].id === ruleId) {
                    _rules.splice(i, 1);
                    setRules(_rules);
                    setHasChanged(true);
                    break;
                }
            }
        };
    }

    function onReplaceRule(rule: InvoiceUploadRule) {
        const _rules = rules.slice();
        for (let i = 0; i < _rules.length; i++) {
            if (_rules[i].id === rule.id) {
                _rules.splice(i, 1, rule);
                setRules(_rules);
                setHasChanged(true);
                break;
            }
        }
    }

    function onChangeProactiveUpload(value: boolean) {
        if (value === proactiveEnabled) return;
        setProactiveEnabled(value);
        setHasChanged(true);
    }

    function onChangeAutoRespond(value: boolean) {
        if (value === respondEnabled) return;
        setRespondEnabled(value);
        setHasChanged(true);
    }

    function onChangeAutoRespondOld(value: boolean) {
        if (value === respondOldEnabled) return;
        setRespondOldEnabled(value);
        setHasChanged(true);
    }

    function onChangeOldNumPrefix(value: string) {
        if (value === oldNumPrefix) return;
        setOldNumPrefix(value);
        setHasChanged(true);
    }

    function onChangeOldNumStart(value: string) {
        if (value === oldNumStart) return;
        setOldNumStart(value);
        setHasChanged(true);
    }

    function onDenyPageChange(id: number | null, callback: PermissionCallback) {
        if (hasChanged)
            onSubmit(() => {
                callback(id, true);
            })();
        else callback(id, true);
    }

    function onSubmit(callback?: VoidFunction) {
        return function () {
            if (!account) return;
            setLoading(true);
            account.settings.proactivelyUploadInvoices = proactiveEnabled;
            account.settings.autoRespondInvoiceRequests = respondEnabled;
            account.settings.autoRespondOldOrders = respondOldEnabled;
            account.settings.oldOrderInvoicePrefix = oldNumPrefix;
            account.settings.oldOrderInvoiceStart = oldNumStart;
            account.settings.notifyMissingInvoice = notificationEnabled;
            account.settings.notifyMissingInvoiceMail = notifyMail;
            account.settings.invoiceUploadRules = rules;
            account.update(
                undefined,
                undefined,
                undefined,
                (account: Account, success: boolean, data: ResponseData) => {
                    setLoading(false);
                    if (success) {
                        context.onChangeContext({account});
                        showSnackbar(t('invoiceUploadSettings.savedSuccess', 'De instellingen zijn opgeslagen'));
                        setHasChanged(false);
                        callback?.();
                    } else {
                        if (data.handled) return;
                        showDialog(
                            createErrorMessage(
                                data.message ??
                                    t(
                                        'invoiceUploadSettings.saveFailed',
                                        'Het opslaan van de instellingen is niet gelukt.'
                                    )
                            ),
                            t('invoiceUploadSettings.saveFailedTitle', 'Instellingen opslaan mislukt')
                        );
                    }
                }
            );
        };
    }

    function onChangeNotification(value: boolean) {
        if (value === notificationEnabled) return;
        setNotificationEnabled(value);
        setHasChanged(true);
    }

    useEffect(() => {
        setRules(context.accountContext.account?.getInvoiceUploadRules() ?? []);
        setHasChanged(false);
    }, [context.accountContext.account]);

    useEffect(() => {
        if (context.accountContext.account)
            onChangeProactiveUpload(context.accountContext.account.settings.proactivelyUploadInvoices);
    }, [context.accountContext.account?.settings.proactivelyUploadInvoices]);

    useEffect(() => {
        if (context.accountContext.account)
            onChangeAutoRespond(context.accountContext.account.settings.autoRespondInvoiceRequests);
    }, [context.accountContext.account?.settings.autoRespondInvoiceRequests]);

    return (
        <>
            <InvoiceUploadSettings
                onAddRule={onAddRule}
                onUpdateRule={onReplaceRule}
                onDeleteRule={onDeleteRule}
                onSubmit={onSubmit()}
                onChangeProactiveUpload={onChangeProactiveUpload}
                onChangeAutoRespond={onChangeAutoRespond}
                onChangeNotifyMail={onChangeNotifyMail}
                onChangeNotification={onChangeNotification}
                onChangeOldNumPrefix={onChangeOldNumPrefix}
                onChangeAutoRespondOld={onChangeAutoRespondOld}
                onChangeOldNumStart={onChangeOldNumStart}
                notifyMail={notifyMail}
                loading={loading}
                respondOldEnabled={respondOldEnabled}
                oldNumPrefix={oldNumPrefix}
                oldNumStart={oldNumStart}
                rules={rules}
                hasChanged={hasChanged}
                proactiveEnabled={proactiveEnabled}
                respondEnabled={respondEnabled}
                notificationEnabled={notificationEnabled}
                noSubTitle={noSubTitle}
            />
            <PageChangePermission
                changed={hasChanged}
                onDeny={onDenyPageChange}
                denyText='Ja, wijzigingen opslaan'
                allowText='Nee, pagina verlaten'
                allowSecondary={true}
            >
                De wijzigingen gaan verloren als je de pagina verlaat. Wil je de wijzigingen eerst opslaan?
            </PageChangePermission>
        </>
    );
}
